import React, { useState } from 'react';
import { css } from '@emotion/css';
import { Subscribe } from "unstated";
import TemplateFormSectionModel from '../../../../shared/models/templating/api/TemplateFormSectionModel';
import TemplateFormFieldModel from '../../../../shared/models/templating/api/TemplateFormFieldModel';
import TemplateFormFieldValueModel from '../../../../shared/models/templating/api/TemplateFormFieldValueModel';
import CheckboxField from '../../../../shared/components/forms/CheckboxField';
import TextField from '../../../../shared/components/forms/TextField';
import TextAreaField from '../../../../shared/components/forms/TextAreaField';
import SsmlField from '../../../../shared/components/forms/SsmlField/SsmlField';
import SelectField from '../../../../shared/components/forms/SelectField';
import CheckboxGroup from '../../../../shared/components/forms/CheckboxGroup';
import RadioGroup from '../../../../shared/components/forms/RadioGroup';
import MediaContainer from '../../../../shared/state/containers/MediaContainer';
import FileUploadField from '../../../../shared/components/forms/FileUploadField';
import FormFieldCard from '../../../../shared/components/forms/FormFieldCard';
import PanelContainer from '../../../../shared/components/structure/Panels/PanelContainer';
import TimeRangeField from '../../../../shared/components/forms/TimeRangeField';
import SingleDayTimeRangeField from '../../../../shared/components/forms/SingleDayTimeRangeField';
import SpecialDateField from '../../../../shared/components/forms/SpecialDateField';
import ScrollablePanelBody from '../../../../shared/components/structure/ScrollablePanelBody';
import { color_shades_dark, color_shades_lighter } from '../../../../shared/constants/colors';
import Switch from '../../../../shared/components/forms/Switch';
import Button from '../../../../shared/components/general/Button';
import AppliedApplicationTemplateContainer from '../../../../shared/state/containers/AppliedApplicationTemplateContainer';
import AppliedApplicationTemplateModel from '../../../../shared/models/templating/api/AppliedApplicationTemplateModel';
import styled from '@emotion/styled';
import TemplateInstructionsPanel from './TemplateInstructionsPanel';
import { breakpoint_small } from '../../../../shared/constants/breakpoints';
import TemplateSaveChangesModal from '../../../../shared/components/templating/TemplateSaveChangesModal';
import { getVisibleFields } from '../../../../shared/utils/sectionUtils';
import ApplicationContainer from '../../../../shared/state/containers/ApplicationContainer';
import { isDeprecatedSingleDayTimeRangeField, isDeprecatedSpecialDateField } from '../../../../shared/utils/fieldTypeUtils';

interface AppliedApplicationTemplateFormProps {
    section: TemplateFormSectionModel
    onFieldValueChange: (field: TemplateFormFieldModel, value: string) => void
    applicationId: string
    values?: TemplateFormFieldValueModel[]
    fieldsOnly?: boolean
    preview?: boolean
    appliedAppTemplateContainer: AppliedApplicationTemplateContainer
    appliedAppTemplate: AppliedApplicationTemplateModel
    combinedRequiredFields: TemplateFormFieldModel[]
    completeFields: TemplateFormFieldModel[]
    submitForm: () => void
    setFieldValue: (fieldName: string, value: string) => void
    onPrevious: () => void
    recentlyAddedModuleFormFields?: TemplateFormFieldModel[]
    showAdminFields?: boolean
    templateFormValues?: TemplateFormFieldValueModel[]
    appContainer: ApplicationContainer
    onFieldValidationChange?: (fieldId: string, hasErrors: boolean) => void
}

const AppliedApplicationTemplateSectionForm: React.FC<AppliedApplicationTemplateFormProps> = (props) => {

    const [showSaveChangesModal, setShowSaveChangesModal] = useState(false);

    const isRecentlyAddedModuleFormField = (field: TemplateFormFieldModel) => {
        return props.recentlyAddedModuleFormFields?.some(f => f.id == field.id);
    };

    const getValue = (field: TemplateFormFieldModel) => {
        const foundField = props.values?.find(v => (field?.name && v?.dynamicFormFieldName == field?.name) || (field?.id && v?.templateFormFieldId == field?.id));
        return foundField?.value;
    };

    const getFieldName = (field: TemplateFormFieldModel): string => {
        return field.name ?? field.id;
    };

    const handleChange = (field: TemplateFormFieldModel, value) => {
        props.onFieldValueChange(field, value);
    };

    const renderCheckbox = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <CheckboxField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    checked={getValue(field) == 'true'}
                    onChange={(e) => handleChange(field, e.target.checked.toString())}
                    className={checkboxField} />
            </div>
        )
    };

    const renderSwitch = (field: TemplateFormFieldModel) => {
        return (
            <div>
                <div className={label}>{field.label}</div>
                <Switch
                    className={switchContainer}
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    onChange={(checked) => handleChange(field, checked.toString())}
                    checked={getValue(field) == 'true'}
                    label={field.label}
                />
            </div>
        )
    };

    const renderShortText = (field: TemplateFormFieldModel, type?: string) => {
        if (isDeprecatedSingleDayTimeRangeField(field, props.appContainer, props.appliedAppTemplate)) {
            return (
                <SingleDayTimeRangeField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getValue(field) ?? field.defaultValue}
                    onChange={(value) => handleChange(field, value)}
                    onBlur={() => { }}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                />
            )
        }

        if (isDeprecatedSpecialDateField(field, props.appContainer, props.appliedAppTemplate)) {
            return (
                <SpecialDateField
                    key={field.id ?? field.name}
                    name={`fields.${getFieldName(field)}`}
                    label={field.label}
                    value={getValue(field) ?? field.defaultValue}
                    onChange={(value) => handleChange(field, value)}
                    onBlur={() => { }}
                    onValidationChange={(hasErrors) => {
                        if (props.onFieldValidationChange) {
                            props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                        }
                    }}
                />
            )
        }

        return (
            <TextField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getValue(field) ?? field.defaultValue}
                type={type}
                onChange={(e) => handleChange(field, e.target.value)}
                onBlur={() => { }}
                label={field.label}
                placeholder={field.placeholder} />
        )
    };



    const renderLongText = (field: TemplateFormFieldModel) => {
        return (
            <TextAreaField
                showResponseVariableTest
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getValue(field) ?? field.defaultValue}
                onChange={(e) => handleChange(field, e.target.value)}
                defaultValue={field.defaultValue}
                onBlur={() => { }}
                label={field.label}
                placeholder={field.placeholder} />
        )
    };

    const renderSsmlField = (field: TemplateFormFieldModel) => {
        return (
            <SsmlField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                value={getValue(field) ?? field.defaultValue ?? ''}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                label={field.label}
                placeholder={field.placeholder}
                applicationId={props.applicationId} />
        )
    };

    const renderSelect = (field: TemplateFormFieldModel) => {
        return (
            <SelectField
                key={field.id ?? field.name}
                className={selectField}
                onChange={(e: any) => handleChange(field, e.value)}
                onBlur={() => { }}
                name={`fields.${getFieldName(field)}`}
                value={getValue(field)}
                label={field.label}
                placeholder={field.placeholder}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
            />
        )
    };

    const renderCheckboxGroup = (field: TemplateFormFieldModel) => {
        // note: checkboxes use pipe delimited values from the core value
        return (
            <CheckboxGroup
                key={field.id ?? field.name}
                onChange={(e, c) => {
                    var val = getValue(field);
                    if (c) {
                        val += `|${e.value}`;
                    } else {
                        val = val.replace(e.value, '');
                    }
                    handleChange(field, val)
                }}
                values={getValue(field)?.split('|')}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
            />
        )
    };

    const renderRadioGroup = (field: TemplateFormFieldModel) => {
        return (
            <RadioGroup
                key={field.id ?? field.name}
                onChange={(e) => handleChange(field, e.value)}
                value={getValue(field)}
                options={field.options.map(o => ({ value: o.value, label: o.label }))}
            />
        )
    };

    const renderFileUpload = (field: TemplateFormFieldModel) => {
        return (
            <Subscribe to={[MediaContainer]}>
                {(mediaContainer: MediaContainer) => (
                    <FileUploadField
                        onChange={(e) => handleChange(field, e.url)}
                        disabled={false}
                        field={field}
                        mediaContainer={mediaContainer}
                        applicationId={props.applicationId}
                        value={getValue(field)}
                        preview={props.preview}
                    />
                )}
            </Subscribe>
        )
    };

    const renderTimeRange = (field: TemplateFormFieldModel) => {
        return (
            <TimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getValue(field) ?? field.defaultValue}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
            />
        )
    };

    const renderSingleDayTimeRange = (field: TemplateFormFieldModel) => {
        return (
            <SingleDayTimeRangeField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getValue(field) ?? field.defaultValue}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        )
    };

    const renderSpecialDate = (field: TemplateFormFieldModel) => {
        return (
            <SpecialDateField
                key={field.id ?? field.name}
                name={`fields.${getFieldName(field)}`}
                label={field.label}
                value={getValue(field) ?? field.defaultValue}
                onChange={(value) => handleChange(field, value)}
                onBlur={() => { }}
                onValidationChange={(hasErrors) => {
                    if (props.onFieldValidationChange) {
                        props.onFieldValidationChange(field.id ?? field.name, hasErrors);
                    }
                }}
            />
        )
    };

    const renderSetupProperty = (field: TemplateFormFieldModel) => {
        switch (field.fieldType) {
            case "ShortTextField":
                return renderShortText(field);
            case "NumberTextField":
                return renderShortText(field, 'number');
            case "DateTextField":
                return renderShortText(field, 'date');
            case "LongTextField":
                return renderLongText(field);
            case "SwitchField":
                return renderSwitch(field);
            case "SelectField":
                return renderSelect(field);
            case "CheckboxGroupField":
                return renderCheckboxGroup(field);
            case "RadioGroupField":
                return renderRadioGroup(field);
            case "CheckboxField":
                return renderCheckbox(field);
            case "FileUploadField":
                return renderFileUpload(field);
            case "TimeRangeField":
                return renderTimeRange(field);
            case "SingleDayTimeRangeField":
                return renderSingleDayTimeRange(field);
            case "SpecialDateField":
                return renderSpecialDate(field);
            case "SSMLField":
            case "SSMLTextField":
                return renderSsmlField(field);
        }
    };

    const renderFields = () => {
        // Use the consolidated helper function to get visible fields
        // This applies both associatedVariable and admin field filtering
        const fields = getVisibleFields(
            props.section,
            props.showAdminFields ?? true,
            props.values
        );

        return (
            <div className={setupSectionContainer}>
                <h3>{props.section.title}</h3>
                {fields.map((p, subIdx) => {
                    return (
                        <div key={`setupSection_${subIdx}`} className={setupPropertyContainer}>
                            <FormFieldCard
                                title={p.title}
                                tip={p.tip}
                                required={p.isRequired}
                                isRecentlyAddedModuleFormField={isRecentlyAddedModuleFormField(p)}
                            >
                                {renderSetupProperty(p)}
                            </FormFieldCard>
                        </div>
                    )
                })}
            </div>
        )
    };

    const isLoading = props.appliedAppTemplateContainer.state.isLoading || props.appliedAppTemplateContainer.state.isSaving;

    const handleSaveForm = () => {
        setShowSaveChangesModal(true);
    };

    const performSave = async () => {
        await props.appliedAppTemplateContainer.saveTemplateFields(
            props.applicationId,
            props.appliedAppTemplate.id,
            props.templateFormValues || props.values || [],
            {
                finalize: false,
                useModifiedFieldsOnly: true
            }
        );
    };

    const closeSaveChangesModal = () => {
        setShowSaveChangesModal(false);
    };

    return (
        <>
            <ScrollablePanelBody autoHeight={true} autoHeightMax={"100vh - 136px"}>
                <SectionFormWrapper>
                    <PanelContainer className={panelContainerStyle}>
                        <PanelWrapper>
                            {renderFields()}
                            <ButtonWrapper>
                                {/* These are the buttons in the bottom left of the Spark form section */}
                                <SaveButton
                                    loading={isLoading}
                                    themes={["secondary"]}
                                    text="Save"
                                    type="button"
                                    onClick={handleSaveForm}
                                />
                                <NextButton
                                    disabled={
                                        props.appliedAppTemplate?.templateForm?.templateConfiguration?.dynamicFormUrl != null &&
                                        props.combinedRequiredFields.length != props.completeFields.length &&
                                        !isLoading
                                    }
                                    loading={isLoading}
                                    themes={["primary"]}
                                    text="Next"
                                    type="button"
                                    onClick={props.submitForm}
                                />
                            </ButtonWrapper>
                        </PanelWrapper>
                        {props.section.instructionsMarkdown && (
                            <RightPanelWrapper>
                                <TemplateInstructionsPanel
                                    title="Additional Instructions"
                                    instructionsMarkdown={props.section.instructionsMarkdown}
                                />
                            </RightPanelWrapper>
                        )}
                    </PanelContainer>
                </SectionFormWrapper>
            </ScrollablePanelBody>
            <TemplateSaveChangesModal
                showSaveChangesModal={showSaveChangesModal}
                closeModal={closeSaveChangesModal}
                changedFields={props.appliedAppTemplateContainer.getChangedFieldsForDisplay(
                    props.appliedAppTemplate.id,
                    props.templateFormValues || props.values || []
                )}
                submitChanges={performSave}
                isLoading={props.appliedAppTemplateContainer.state.isSaving}
                hasErrors={false}
            />
        </>
    );
};

const SectionFormWrapper = styled.div`
    display: flex;
`;

const PanelWrapper = styled.div`
    flex: row;
    flex: 1;
`;

const RightPanelWrapper = styled.div`
    flex: row;
    flex: 1;
    ${breakpoint_small} {
        display: none;
    }
`;

const NextButton = styled(Button)`
    margin-top: 0px;
    margin-left: 0px;
`;

const SaveButton = styled(Button)`
    margin-top: 0px;
`;

const panelContainerStyle = css`
    display: flex;
`;

const selectField = css`
    padding-top: 16px;
  `;

const checkboxField = css`
    padding-left: 16px;
    .tooltip {
      margin-top: 3px;
    }
  `;

const ButtonWrapper = styled.div`
    display: flex;
    justify-content: flex-start;
    margin: 0 0 32px 18px;
    ${breakpoint_small} {
      justify-content: flex-end;
    }
  `;

const setupSectionContainer = css`
    padding: 32px;
    h3 {
      margin-bottom: 16px;
      font-style: normal;
      font-weight: normal;
      font-size: 18px;
      line-height: 24px;
    }
    ${breakpoint_small} {
      padding: 16px;
      h3 {
        margin-bottom: 0px;
      }
    }
  `;

const label = css`
    font-family: Muli;
    font-size: 14px;
    font-weight: 600;
    font-style: normal;
    font-stretch: normal;
    letter-spacing: normal;
    text-align: left;
    margin: 8px 16px;
  `;

const switchContainer = css`
    margin-bottom: 32px;
  `;

const setupPropertyContainer = css`
    padding: 16px 0px;
    position: relative;
    .field-triangle {
      position: absolute;
      width: 24px;
      height: 24px;
      background: ${color_shades_lighter};
      right: -44px;
      top: 64px;
      transform: rotate(45deg);
      border-left: 1px solid ${color_shades_dark};
      border-bottom: 1px solid ${color_shades_dark};
    }
  `;

export default AppliedApplicationTemplateSectionForm;

